import React, { useMemo } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  OnChangeFn,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';
import { INamedFilterFieldsFragment } from '../../../utils/apollo/graphqlSchema';
import Table from '../../../components/Table/Table';
import EditableTextArea from '../../../components/EditableTextArea';
import { EntityCategories } from '../../../components/Categories';
import { FavoriteVersionEntity } from '../../../components/Favorite';
import FormCreator from '../../pageForm/FormCreator';
import FormEditor from '../../pageForm/FormEditor';
import TimezonePopup from '../../../components/TimezonePopup';
import EntityInfoPopover from '../../../components/EntityInfoPopover';
import BuilderInfo from '../../../components/BuilderInfo';
import ActionsMenu from '../../../components/ActionsMenu';
import { SingleSelectOption } from '../../../components/Dropdown/SingleSelect';
import { defaultPermissionTooltipOption } from '../../../components/KubitTooltip/globalTooltips';
import { globalFormat } from '../../../utils/formatting';
import {
  entityDescriptionSchema,
  entityNameSchema,
} from '../../../components/EditableTextArea/validation';
import { filtersColumnsConfig } from './tableStylesConfig';
import { FavoriteEntityType } from '../../../utils/apollo/graphqlSchema';
import { IApplyFilterTag } from './types';
import useSyndicateTimezoneLabel from '../../../utils/hooks/useSyndicateTimezoneLabel';
import * as FormUI from '../../pageForm/FormUI';
import {
  HeaderCheckbox,
  RowCheckbox,
} from '../../../components/Table/TableCheckbox';

const initialState = {
  sorting: [
    {
      id: 'name',
      desc: false,
    },
  ],
};

const columnHelper = createColumnHelper<INamedFilterFieldsFragment>();

interface IProps {
  filters: INamedFilterFieldsFragment[];
  schemaId: string;
  onApplyTags: (args: IApplyFilterTag) => Promise<void>;
  onNameChange: (
    namedFilter: INamedFilterFieldsFragment,
    newValue: string,
  ) => Promise<void>;
  onDescriptionChange: (
    namedFilter: INamedFilterFieldsFragment,
    newValue: string,
  ) => Promise<void>;
  onDeleteFilter: (filter: INamedFilterFieldsFragment) => void;
  rowSelection: RowSelectionState;
  setRowSelection: OnChangeFn<RowSelectionState>;
  isPermittedToEditNamedFilter: boolean;
  isPermittedToDeleteNamedFilter: boolean;
  isPermittedToAssignCategory: boolean;
}

const FiltersTable = ({
  filters,
  schemaId,
  onApplyTags,
  onNameChange,
  onDescriptionChange,
  onDeleteFilter,
  rowSelection,
  setRowSelection,
  isPermittedToEditNamedFilter,
  isPermittedToDeleteNamedFilter,
  isPermittedToAssignCategory,
}: IProps) => {
  const shortTimeZone = useSyndicateTimezoneLabel();
  const { dateFormat, timeFormat } = globalFormat.getGlobalFormat();

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        id: 'select',
        header: ({ table }) => <HeaderCheckbox table={table} />,
        cell: ({ row }) => <RowCheckbox row={row} />,
        meta: filtersColumnsConfig.select,
        enableSorting: false,
      }),
      columnHelper.accessor('name', {
        id: 'name',
        header: 'Name',
        cell: (info) => (
          <FormUI.OverflowingTextWrapper>
            <EditableTextArea
              isBold
              disabled={info.row.original.isDisabled}
              placeholder="Add Name..."
              value={info.getValue()}
              onChange={(newValue) => onNameChange(info.row.original, newValue)}
              defaultRows={2}
              validation={entityNameSchema}
              isReadOnly={!isPermittedToEditNamedFilter}
            />
          </FormUI.OverflowingTextWrapper>
        ),
        meta: filtersColumnsConfig.name,
        enableSorting: true,
      }),
      columnHelper.accessor('description', {
        id: 'description',
        header: 'Description',
        cell: (info) => (
          <FormUI.OverflowingTextWrapper>
            <EditableTextArea
              disabled={info.row.original.isDisabled}
              placeholder={'Add Description...'}
              value={info.getValue()}
              onChange={(newValue) =>
                onDescriptionChange(info.row.original, newValue)
              }
              validation={entityDescriptionSchema}
              isReadOnly={!isPermittedToEditNamedFilter}
            />
          </FormUI.OverflowingTextWrapper>
        ),
        meta: filtersColumnsConfig.description,
        enableSorting: true,
      }),
      columnHelper.accessor('categories', {
        id: 'tags',
        header: 'Tags',
        cell: (info) => (
          <EntityCategories
            schemaId={schemaId}
            isDisabled={!isPermittedToAssignCategory}
            entityCategories={info.getValue()}
            onApply={(newTags, isManaged) =>
              onApplyTags({
                entities: [info.row.original],
                newTags,
                isManaged,
                schemaId,
              })
            }
            isManaged={info.row.original.isManaged}
          />
        ),
        meta: filtersColumnsConfig.tags,
        enableSorting: true,
      }),
      columnHelper.accessor('id', {
        id: 'favorite',
        header: 'Favorite',
        cell: (info) => (
          <FavoriteVersionEntity
            entityType={FavoriteEntityType.Filter}
            entity={info.row.original}
          />
        ),
        meta: filtersColumnsConfig.favorite,
        enableSorting: true,
      }),
      columnHelper.accessor('creator', {
        id: 'creator',
        header: 'Creator',
        cell: (info) =>
          info.getValue() && (
            <FormCreator
              label={info.getValue().name}
              picture={info.getValue().picture}
              createdAt={info.row.original.createdAt}
            />
          ),
        meta: filtersColumnsConfig.creator,
        enableSorting: true,
      }),
      columnHelper.accessor('editor', {
        id: 'editor',
        header: 'Last Edited',
        cell: (info) =>
          info.getValue() && (
            <FormEditor
              label={info.getValue().name}
              picture={info.getValue().picture}
              lastEdited={info.row.original.editedAt}
            />
          ),
        meta: filtersColumnsConfig.editor,
        enableSorting: true,
      }),
      columnHelper.accessor('editedAt', {
        id: 'time',
        header: () => `Last Edited Time (${shortTimeZone})`,
        cell: (info) => (
          <FormUI.OverflowingTextWrapper>
            <TimezonePopup
              timestamp={info.getValue()}
              dateFormat={dateFormat}
              timeFormat={timeFormat}
              placement="bottom-end"
              showTimezone
            />
          </FormUI.OverflowingTextWrapper>
        ),
        meta: filtersColumnsConfig.time,
        enableSorting: true,
      }),
      columnHelper.accessor('id', {
        id: 'actions',
        header: () => null,
        cell: (info) => (
          <FormUI.ActionsContainer>
            <EntityInfoPopover
              placement="bottom-end"
              closeOnAncestorScroll={true}
            >
              <BuilderInfo kind="namedFilter" namedFilter={info.row.original} />
            </EntityInfoPopover>
            <ActionsMenu>
              <SingleSelectOption
                onClick={() => onDeleteFilter(info.row.original)}
                iconName="trash"
                label={'Delete'}
                disabled={!isPermittedToDeleteNamedFilter}
                tooltipOption={
                  !isPermittedToDeleteNamedFilter
                    ? defaultPermissionTooltipOption
                    : undefined
                }
              />
            </ActionsMenu>
          </FormUI.ActionsContainer>
        ),
        meta: filtersColumnsConfig.actions,
      }),
    ],
    [
      onNameChange,
      onDescriptionChange,
      onApplyTags,
      onDeleteFilter,
      schemaId,
      isPermittedToEditNamedFilter,
      isPermittedToDeleteNamedFilter,
      isPermittedToAssignCategory,
      dateFormat,
      timeFormat,
      shortTimeZone,
    ],
  );

  const table = useReactTable({
    data: filters,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection,
    },
    initialState,
    getRowId: (row) => row.id,
  });

  return (
    <Table
      headerGroups={table.getHeaderGroups()}
      rows={table.getRowModel().rows}
    />
  );
};

export default FiltersTable;
